import React, { useState } from "react";
import { Button } from "@/ui/components/ui/button.tsx";
import { Plus } from "lucide-react";
import { useGetAllPolygons } from "@/polygon/api/queries/polygon.queries.ts";
import { usePolygonOperations } from "@/polygon/hooks";
import { PolygonTable, UploadPolygonModal, PolygonStatistics } from "@/polygon/components";
import { Card, CardContent } from "@/ui/components/ui/card.tsx";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { useTranslation } from "react-i18next";

const PolygonsPage: React.FC = () => {
  const { t } = useTranslation("polygon");
  const { data: polygons, isLoading, error } = useGetAllPolygons();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { selectedOrganizationId } = useOrganizationStore();
  const { data: orgsData, isLoading: orgsLoading } = useGetOrganizations();

  const {
    uploadForm,
    isAnalyzing,
    createMutation,
    deleteMutation,
    handleDelete,
    handleFileChange,
    handleUpload,
    resetUploadForm,
    updateUploadForm,
  } = usePolygonOperations();

  const organization = orgsData?.data?.find(org => org.id === selectedOrganizationId);

  // Set breadcrumbs using the hook
  useSetBreadcrumbs([
    {
      path: "/",
      breadcrumb: organization?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: orgsLoading,
    },
    {
      path: "/polygons",
      breadcrumb: t("breadcrumbs.polygons"),
    },
  ]);

  const handleUploadSuccess = () => {
    setIsUploadModalOpen(false);
    resetUploadForm();
  };

  const handleCancel = () => {
    setIsUploadModalOpen(false);
    resetUploadForm();
  };

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="container mx-auto p-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-red-600">
                  {t("page.loadError", { message: error.message })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">{t("page.title")}</h1>
              <p className="text-muted-foreground">
                {t("page.description")}
              </p>
            </div>

            <Button
              className="gap-2"
              onClick={() => setIsUploadModalOpen(true)}
            >
              <Plus className="h-4 w-4" />
              {t("page.upload")}
            </Button>
          </div>

          {/* Statistics */}
          <PolygonStatistics
            polygons={polygons?.data}
            isLoading={isLoading}
          />

          {/* Polygons Table */}
          <PolygonTable
            polygons={polygons?.data}
            isLoading={isLoading}
            onDelete={handleDelete}
            isDeletePending={deleteMutation.isPending}
          />

          {/* Upload Modal */}
          <UploadPolygonModal
            isOpen={isUploadModalOpen}
            onOpenChange={setIsUploadModalOpen}
            uploadForm={uploadForm}
            isAnalyzing={isAnalyzing}
            isUploading={createMutation.isPending}
            onFileChange={handleFileChange}
            onFormChange={updateUploadForm}
            onUpload={() => {
              handleUpload();
              handleUploadSuccess();
            }}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default PolygonsPage;